import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import DropDownWithSearch from "../DropDownWithSearch";
import "./index.scss";
import UserCardAvatar from "../UserCardAvatar";
import OnlineStatusExpanded from "../OnlineStatusExpanded";
import { TabsList, TabsTrigger } from "../Tabs";
import classNames from "../../../utils/classNames";
import { resolveUserDisplayName } from "../../../utils/userIdentity";

const FriendSearchDropdown = ({
                                  dropdownRef,
                                  searchTerm,
                                  handleSearchChange,
                                  filteredFriends,
                                  startChatWithFriend,
                                  chatMode,
                                  onChatModeChange,
                                  selectedGroupUserIds,
                                  onToggleGroupUser,
                                  onCreateGroupChat,
                                  groupCreateDisabled,
                                  groupCreateBusy,
                                  groupSelectionLimit,
                                  groupError,
                                  onClose,
                                  closeLabel,
                                  resolvePresence,
                              }) => {
    const { t } = useTranslation();
    const selectedGroupUserIdSet = useMemo(
        () => new Set((selectedGroupUserIds || []).map((userId) => String(userId))),
        [selectedGroupUserIds]
    );
    const isGroupMode = chatMode === "group";
    const selectedCount = selectedGroupUserIdSet.size;
    const handleChatModeChange = (nextMode) => {
        if (nextMode !== chatMode) {
            onChatModeChange?.(nextMode);
        }
    };

    const getPresence = (friend) => {
        const fallbackOnline = typeof friend.online === "boolean" ? friend.online : false;
        const fallbackLastSeen = friend.lastSeen ?? null;
        const candidateId = friend?.userId ?? friend?.id;
        if (!resolvePresence || !candidateId) {
            return {
                online: fallbackOnline,
                lastSeen: fallbackLastSeen,
            };
        }
        const resolved = resolvePresence(candidateId);
        return {
            online: resolved?.online ?? fallbackOnline,
            lastSeen: resolved?.lastSeen ?? fallbackLastSeen,
        };
    };

    const renderFriendItem = (friend, index) => {
        const key = (friend.userId && String(friend.userId)) || friend.id || index;
        let formattedSince = null;
        if (friend.since) {
            const parsed = new Date(friend.since);
            if (!Number.isNaN(parsed.getTime())) {
                formattedSince = parsed.toLocaleDateString([], { month: "short", year: "numeric" });
            }
        }

        const userId = friend.userId ?? friend.id;
        const normalizedUserId = userId !== undefined && userId !== null ? String(userId) : "";
        const displayName = resolveUserDisplayName(friend, t("chatPage.anonymousUser"));
        const isSelected = selectedGroupUserIdSet.has(normalizedUserId);
        const isSelectionDisabled = isGroupMode
            && !isSelected
            && selectedCount >= groupSelectionLimit;
        const rowClassName = classNames(
            "friend-search-option",
            isGroupMode && "friend-search-option--selectable",
            isSelected && "friend-search-option--selected",
            isSelectionDisabled && "friend-search-option--disabled"
        );

        const avatar = (
            <UserCardAvatar
                userId={userId}
                username={displayName}
                alt={displayName || t("chatPage.newChatDialog.avatarAlt")}
                className="friend-search-option__avatar"
                avatarClassName="friend-search-option__avatar-media"
                radius="50%"
                enableProfilePreview={!isGroupMode}
            />
        );

        const meta = (
            <div className="friend-search-option__meta">
                <span className="friend-search-option__name">{displayName}</span>
                <OnlineStatusExpanded
                    {...getPresence(friend)}
                    className="friend-search-option__status"
                />
                {formattedSince && (
                    <span className="friend-search-option__since">
                        {t("chatPage.newChatDialog.friendSince", { date: formattedSince })}
                    </span>
                )}
            </div>
        );

        if (isGroupMode) {
            return (
                <label
                    key={key}
                    className={rowClassName}
                    aria-disabled={isSelectionDisabled}
                >
                    {avatar}
                    <span className="friend-search-option__checkbox">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isSelectionDisabled}
                            onChange={() => onToggleGroupUser?.(normalizedUserId)}
                            aria-label={t("chatPage.newChatDialog.selectFriend", {
                                name: displayName,
                            })}
                        />
                        <span className="friend-search-option__checkbox-control" aria-hidden="true" />
                    </span>
                    {meta}
                </label>
            );
        }

        return (
            <button
                key={key}
                type="button"
                className={rowClassName}
                onClick={() => startChatWithFriend(userId, displayName)}
            >
                {avatar}
                {meta}
                <span className="friend-search-option__cta">{t("chatPage.newChatDialog.start")}</span>
            </button>
        );
    };

    const header = (
        <TabsList
            activeTab={chatMode}
            setActiveTab={handleChatModeChange}
            aria-label={t("chatPage.newChatDialog.modeAria")}
            className="friend-search-mode-tabs"
        >
            <TabsTrigger value="direct">
                {t("chatPage.newChatDialog.modes.direct")}
            </TabsTrigger>
            <TabsTrigger value="group">
                {t("chatPage.newChatDialog.modes.group")}
            </TabsTrigger>
        </TabsList>
    );

    const footer = isGroupMode ? (
        <div className="friend-search-group-footer">
            <div className="friend-search-group-footer__status">
                <span>{t("chatPage.newChatDialog.selectedCount", {
                    count: selectedCount,
                    limit: groupSelectionLimit,
                })}</span>
                {groupError ? (
                    <span className="friend-search-group-footer__error" role="alert">
                        {groupError}
                    </span>
                ) : null}
            </div>
            <button
                type="button"
                className="friend-search-group-footer__create"
                onClick={onCreateGroupChat}
                disabled={groupCreateDisabled || groupCreateBusy}
            >
                {groupCreateBusy
                    ? t("chatPage.newChatDialog.creatingGroup")
                    : t("chatPage.newChatDialog.createGroup")}
            </button>
        </div>
    ) : null;

    // Rendered through the global #portal (like the New Event / New Venue
    // modals) so the dimmed-blur backdrop and the dialog escape the page's
    // stacking context and paint above the fixed app header instead of being
    // clipped under it.
    const content = (
        <>
            <div
                className="friend-search-overlay"
                role="presentation"
                onClick={onClose}
            />
            <DropDownWithSearch
                dropdownRef={dropdownRef}
                searchTerm={searchTerm}
                handleSearchChange={handleSearchChange}
                placeholder={t("chatPage.newChatDialog.searchPlaceholder")}
                items={filteredFriends}
                renderItem={renderFriendItem}
                noItemsMessage={t("chatPage.newChatDialog.empty")}
                className="dropdown-with-search--friend"
                onClose={onClose}
                closeLabel={closeLabel}
                header={header}
                footer={footer}
            />
        </>
    );

    const portalTarget = typeof document !== "undefined"
        ? document.getElementById("portal")
        : null;

    return portalTarget ? createPortal(content, portalTarget) : content;
};

FriendSearchDropdown.propTypes = {
    dropdownRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ]),
    searchTerm: PropTypes.string.isRequired,
    handleSearchChange: PropTypes.func.isRequired,
    filteredFriends: PropTypes.arrayOf(PropTypes.object).isRequired,
    startChatWithFriend: PropTypes.func.isRequired,
    chatMode: PropTypes.oneOf(["direct", "group"]),
    onChatModeChange: PropTypes.func,
    selectedGroupUserIds: PropTypes.arrayOf(PropTypes.string),
    onToggleGroupUser: PropTypes.func,
    onCreateGroupChat: PropTypes.func,
    groupCreateDisabled: PropTypes.bool,
    groupCreateBusy: PropTypes.bool,
    groupSelectionLimit: PropTypes.number,
    groupError: PropTypes.string,
    onClose: PropTypes.func,
    closeLabel: PropTypes.string,
    resolvePresence: PropTypes.func,
};

FriendSearchDropdown.defaultProps = {
    dropdownRef: undefined,
    chatMode: "direct",
    onChatModeChange: undefined,
    selectedGroupUserIds: [],
    onToggleGroupUser: undefined,
    onCreateGroupChat: undefined,
    groupCreateDisabled: true,
    groupCreateBusy: false,
    groupSelectionLimit: 9,
    groupError: null,
    onClose: undefined,
    closeLabel: "Close friend search",
    resolvePresence: undefined,
};

export default FriendSearchDropdown;
